/**
 * Typed API helpers for parkify-sales contract endpoints.
 *
 * The Contract record shape mirrors the Python ContractPayload / Contract
 * models — see parkify-sales/app/services/contract/models.py for the
 * source of truth. Field names round-trip JSON exactly (snake_case in
 * Python, snake_case in TS) so the portal never has to remap keys.
 */

export type ContractTerm = 'monthly' | '1yr' | '2yr' | '3yr' | 'custom'

export type ContractStatus =
  | 'draft'
  | 'sent'
  | 'viewed'
  | 'signed'
  | 'declined'
  | 'expired'
  | 'void'

export interface ContractPayload {
  client_id: string
  operator_name: string
  operator_email: string
  operator_signer_name: string
  operator_signer_title?: string | null
  lot_labels: string[]
  platform_fee_bps: number
  monthly_fee_usd: number
  term: ContractTerm
  term_description?: string | null
  start_date: string // ISO YYYY-MM-DD
  proposal_id?: string | null
}

export interface ContractRecord {
  id: string
  rep_email: string
  rep_name?: string | null
  operator_name: string
  operator_email: string
  client_id: string
  status: ContractStatus
  payload: ContractPayload
  pdf_gcs_path?: string | null
  documenso_envelope_id?: string | null
  documenso_envelope_url?: string | null
  documenso_template_used?: string | null
  signed_pdf_gcs_path?: string | null
  sent_at?: string | null
  viewed_at?: string | null
  signed_at?: string | null
  declined_at?: string | null
  expired_at?: string | null
  decline_reason?: string | null
  proposal_id?: string | null
  template_version?: string | null
  created_at: string
  updated_at: string
}

/** Body the portal POSTs to /contract/from-proposal/<pid>. Mirrors the
 *  Python FromProposalBody. */
export interface FromProposalBody {
  operator_email: string
  operator_signer_name: string
  operator_signer_title?: string
  client_id: string
  lot_labels?: string[]
  platform_fee_bps?: number
  monthly_fee_usd: number
  term: ContractTerm
  term_description?: string
  start_date: string // ISO YYYY-MM-DD
}

function asJsonError(text: string, status: number): Error {
  try {
    const parsed = JSON.parse(text) as { detail?: string; error?: string }
    return new Error(parsed.detail ?? parsed.error ?? `HTTP ${status}`)
  } catch {
    return new Error(text || `HTTP ${status}`)
  }
}

export async function generateContractFromProposal(
  apiBase: string,
  headers: HeadersInit,
  proposalId: string,
  body: FromProposalBody,
): Promise<ContractRecord> {
  const res = await fetch(
    `${apiBase}/contract/from-proposal/${encodeURIComponent(proposalId)}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify(body),
    },
  )
  if (!res.ok) throw asJsonError(await res.text().catch(() => ''), res.status)
  return (await res.json()) as ContractRecord
}

export async function sendContract(
  apiBase: string,
  headers: HeadersInit,
  contractId: string,
): Promise<ContractRecord> {
  const res = await fetch(
    `${apiBase}/contract/${encodeURIComponent(contractId)}/send`,
    { method: 'POST', headers },
  )
  if (!res.ok) throw asJsonError(await res.text().catch(() => ''), res.status)
  return (await res.json()) as ContractRecord
}

export async function listContracts(
  apiBase: string,
  headers: HeadersInit,
  opts: { mine?: boolean } = {},
): Promise<ContractRecord[]> {
  const params = new URLSearchParams()
  if (opts.mine) params.set('mine', 'true')
  const res = await fetch(`${apiBase}/contract/list?${params.toString()}`, {
    headers,
  })
  if (!res.ok) throw asJsonError(await res.text().catch(() => ''), res.status)
  const body = (await res.json()) as { contracts: ContractRecord[] }
  return body.contracts
}

export async function getContract(
  apiBase: string,
  headers: HeadersInit,
  id: string,
): Promise<ContractRecord> {
  const res = await fetch(`${apiBase}/contract/${encodeURIComponent(id)}`, {
    headers,
  })
  if (!res.ok) throw asJsonError(await res.text().catch(() => ''), res.status)
  return (await res.json()) as ContractRecord
}

export function contractPdfUrl(
  apiBase: string,
  id: string,
  token: string,
): string {
  // Same query-param-auth trick the proposal PDF endpoint uses — <a download>
  // can't set Authorization headers, so the JWT rides on the URL.
  const u = new URL(`${apiBase}/contract/${encodeURIComponent(id)}/pdf`)
  u.searchParams.set('access_token', token)
  return u.toString()
}
